// ProfileAvatarUploader — the editable profile picture (#249).
// A large avatar with a hover "change" overlay + a remove action, backed by the
// framework's PUT/DELETE /api/users/me/profile-picture (via the api-kit UserApi).
// After a change it refreshes the auth user so the new avatar appears everywhere
// UserAvatar renders (sidebar, rosters, colleagues…). Represents the logged-in
// user, so it takes no id — the endpoint is always "me".
import React, { useRef, useState } from 'react';
import { Camera, Loader2, Trash2 } from 'lucide-react';
import { getUserApi } from '@/lib/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import ImageCropModal from './ImageCropModal';

const SIZES = {
    md: 'w-20 h-20 text-2xl',
    lg: 'w-24 h-24 text-3xl',
    xl: 'w-28 h-28 text-4xl',
} as const;

// Keep in sync with the framework's image policy — reject early with a clear
// message instead of a round-trip 4xx.
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB

const initialsOf = (name: string): string =>
    name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?';

interface ProfileAvatarUploaderProps {
    name: string;
    pictureUrl?: string | null;
    size?: keyof typeof SIZES;
    className?: string;
}

export const ProfileAvatarUploader: React.FC<ProfileAvatarUploaderProps> = ({
    name,
    pictureUrl,
    size = 'lg',
    className = '',
}) => {
    const { getCurrentUser } = useAuth();
    const { toast } = useToast();
    const inputRef = useRef<HTMLInputElement>(null);
    const [busy, setBusy] = useState<'upload' | 'remove' | null>(null);
    const [imgErrored, setImgErrored] = useState(false);
    // Object URL of the just-picked file while the crop/adjust modal is open.
    const [cropSrc, setCropSrc] = useState<string | null>(null);
    const dims = SIZES[size];
    const resolvedUrl = resolveMediaUrl(pictureUrl);

    const showImage = !!resolvedUrl && !imgErrored;

    const pickFile = () => { if (!busy) inputRef.current?.click(); };

    const onFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        // Reset the input so choosing the SAME file again still fires onChange.
        e.target.value = '';
        if (!file) return;

        if (!ACCEPTED.includes(file.type)) {
            toast({ title: 'Filformat stöds inte', description: 'Använd en JPG-, PNG-, WEBP- eller GIF-bild.', variant: 'destructive' });
            return;
        }
        if (file.size > MAX_BYTES) {
            toast({ title: 'Bilden är för stor', description: 'Välj en bild som är mindre än 5 MB.', variant: 'destructive' });
            return;
        }

        // Open the adjust/crop step — the actual upload happens on confirm.
        setImgErrored(false);
        setCropSrc(URL.createObjectURL(file));
    };

    const closeCropper = () => {
        setCropSrc((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    };

    // Called by the crop modal with the cropped square as a WEBP blob.
    const onCropConfirm = async (blob: Blob) => {
        try {
            setBusy('upload');
            const file = new File([blob], 'profile.webp', { type: 'image/webp' });
            await getUserApi().uploadProfilePicture(file);
            await getCurrentUser(true); // refresh app-wide avatar
            toast({ title: 'Profilbild uppdaterad' });
            closeCropper();
        } catch {
            toast({ title: 'Kunde inte ladda upp bilden', description: 'Försök igen om en stund.', variant: 'destructive' });
        } finally {
            setBusy(null);
        }
    };

    const removePicture = async () => {
        if (busy || !pictureUrl) return;
        try {
            setBusy('remove');
            await getUserApi().deleteProfilePicture();
            await getCurrentUser(true);
            toast({ title: 'Profilbild borttagen' });
        } catch {
            toast({ title: 'Kunde inte ta bort bilden', description: 'Försök igen om en stund.', variant: 'destructive' });
        } finally {
            setBusy(null);
        }
    };

    return (
        <>
        <div className={`flex items-center gap-4 ${className}`}>
            <div className="relative flex-shrink-0">
                <button
                    type="button"
                    onClick={pickFile}
                    disabled={!!busy}
                    aria-label={showImage ? 'Byt profilbild' : 'Ladda upp profilbild'}
                    className={`${dims} group relative rounded-full overflow-hidden flex items-center justify-center
                        font-semibold text-[#1A4D4F] bg-[#A4C3B2] focus:outline-none focus:ring-2
                        focus:ring-[#1A4D4F] focus:ring-offset-2 disabled:cursor-not-allowed transition-shadow`}
                >
                    {showImage ? (
                        <img src={resolvedUrl!} alt={name} onError={() => setImgErrored(true)} className="w-full h-full object-cover" />
                    ) : (
                        <span>{initialsOf(name)}</span>
                    )}

                    {/* Hover / busy overlay */}
                    <span
                        className={`absolute inset-0 flex items-center justify-center bg-black/45 text-white
                            transition-opacity ${busy === 'upload' ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                    >
                        {busy === 'upload'
                            ? <Loader2 className="w-6 h-6 animate-spin" />
                            : <Camera className="w-6 h-6" />}
                    </span>
                </button>

                {/* Remove (only when a picture exists) */}
                {showImage && (
                    <button
                        type="button"
                        onClick={removePicture}
                        disabled={!!busy}
                        aria-label="Ta bort profilbild"
                        className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white shadow-md border border-gray-200
                            flex items-center justify-center text-gray-500 hover:text-red-600 hover:border-red-200
                            disabled:cursor-not-allowed transition-colors"
                    >
                        {busy === 'remove' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                )}

                <input
                    ref={inputRef}
                    type="file"
                    accept={ACCEPTED.join(',')}
                    onChange={onFileChosen}
                    className="hidden"
                />
            </div>

            <div className="min-w-0">
                <button
                    type="button"
                    onClick={pickFile}
                    disabled={!!busy}
                    className="text-sm font-medium text-[#1A4D4F] hover:underline disabled:no-underline disabled:text-gray-400"
                >
                    {showImage ? 'Byt profilbild' : 'Ladda upp profilbild'}
                </button>
                <p className="text-xs text-gray-400 mt-0.5">JPG, PNG, WEBP eller GIF · max 5 MB</p>
            </div>
        </div>

        <ImageCropModal
            open={!!cropSrc}
            imageSrc={cropSrc}
            loading={busy === 'upload'}
            onCancel={closeCropper}
            onConfirm={onCropConfirm}
        />
        </>
    );
};

export default ProfileAvatarUploader;
